"use client";

import Link from "next/link";
import { DataTable } from "@/components/ui/data-table";
import { columns } from "./columns";
import { Button } from "@/components/ui/button";
import { CirclePlus, LoaderCircle, RefreshCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useOrganizationStore } from "@/stores/organization";
import { toast } from "sonner";
import {
  DEFAULT_DEBOUNCE_DELAY,
  DEFAULT_ERROR_TITLE,
  DEFAULT_MISSING_PERMISSION_TITLE,
  DEFAULT_ORGANIZATION_UPDATED_TITLE,
  LIBUM_DOCS_POWERON_PRO_CAPABILITIES_URL,
  TAILWIND_SHAKE_ANIMATION_DELAY,
} from "@/lib/constants";
import { getSubscriptionUrl } from "@/services/subscription.client";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useEffect, useState } from "react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Member } from "@/types/member";
import { useUserStore } from "@/stores/user";
import { useDebounce } from "@/hooks/useDebounce";
import { overrideMessageIf403 } from "@/utils/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

export default function PowerOnProUsers() {
  useDocumentTitle("Users");
  const router = useRouter();
  const isMobile = useIsMobile();
  const isLargeScreen = useMediaQuery("(max-width: 920px)");
  const { user } = useUserStore();
  const {
    organization,
    members,
    subscriptions,
    isLoading,
    setError: setOrganizationError,
    getOrganizationMembers,
    updateOrganizationMember,
  } = useOrganizationStore();
  const [hasActivePowerOnProSubscription, setHasActivePowerOnProSubscription] =
    useState(false);
  const [users, setUsers] = useState<Member[]>([]);
  const [isRefreshBlocked, setIsRefreshBlocked] = useState(false);
  const [loadingMemberId, setLoadingMemberId] = useState<string | null>(null);

  const handleToggleSubscription = async (member: Member, checked: boolean) => {
    if (organization === null || user === null) return;

    const isAdmin = users.find((u) => u.userId === user.id)?.role === "admin";

    // Protect against non-admins and non-owners
    if (user.id !== organization.ownerId && !isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be the organization owner or an admin to manage PowerOn Pro+ seats",
      });
      return;
    }

    try {
      const subscription = subscriptions.find(
        (s) =>
          s.productId === process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
          s.status === "active",
      );
      const availableSeats =
        (subscription?.seats || 0) -
        members.filter((m) => m.isPowerOnProPlus).length;

      if (checked && availableSeats <= 0) {
        if (!subscription) {
          toast.error("No active subscription", {
            description:
              "You need an active subscription to enable PowerOn Pro+",
            action: {
              label: "Subscriptions",
              onClick: async () => {
                startNavigationFeedback();
                router.push("/settings/subscriptions");
              },
            },
          });
          return;
        }
        toast.error("No available seats", {
          description:
            "You have reached the maximum number of PowerOn Pro+ seats",
          action: {
            label: "Add seats",
            onClick: async () => {
              if (organization === null) return;

              try {
                const url = await getSubscriptionUrl(
                  organization.id,
                  organization.customerId,
                );

                if (url !== "" && subscription) {
                  window.open(
                    `${url}/subscriptions/${subscription.id}/update`,
                    "_self",
                  );
                } else {
                  toast.error(DEFAULT_ERROR_TITLE, {
                    description:
                      "We're unable to create a billing session. Please try again later.",
                  });
                }
              } catch (error: any) {
                toast.error(
                  ...overrideMessageIf403(error.message, [
                    DEFAULT_ERROR_TITLE,
                    {
                      description:
                        "There was a problem creating the Stripe billing session. Please try again later.",
                    },
                  ]),
                );
                console.warn(error.message);
                setOrganizationError(error.message);
              }
            },
          },
        });
        return;
      }

      if ((availableSeats > 0 && checked) || !checked) {
        setLoadingMemberId(member.id);
        try {
          await updateOrganizationMember({
            id: member.id,
            organizationId: organization.id,
            isPowerOnProPlus: checked,
          });

          // Display message based on toggle state
          if (checked) {
            toast.success(DEFAULT_ORGANIZATION_UPDATED_TITLE, {
              description: `Added ${member.email} to PowerOn Pro+ seats`,
            });
          } else {
            toast.success(DEFAULT_ORGANIZATION_UPDATED_TITLE, {
              description: `Removed ${member.email} PowerOn Pro+ seats`,
            });
          }
        } finally {
          setLoadingMemberId(null);
        }
      }
    } catch (error: any) {
      console.warn(error.message);
      setLoadingMemberId(null);
    }
  };

  const refreshUsers = async () => {
    if (!organization) return;

    try {
      await getOrganizationMembers(organization.id, true);
    } catch (error: any) {
      console.warn(error.message);
    }
  };

  const debounceRefresh = useDebounce(
    refreshUsers,
    DEFAULT_DEBOUNCE_DELAY,
    () => {
      setIsRefreshBlocked(true);
      setTimeout(
        () => setIsRefreshBlocked(false),
        TAILWIND_SHAKE_ANIMATION_DELAY,
      );
    },
  );

  useEffect(() => {
    setHasActivePowerOnProSubscription(
      subscriptions.find(
        (s) =>
          s.productId === process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
          s.status === "active",
      )
        ? true
        : false,
    );
  }, [subscriptions]);

  useEffect(() => {
    setUsers(
      members
        .filter((m) => m.status === "active")
        .sort((a, b) =>
          a.userId === user?.id ? -1 : b.userId === user?.id ? 1 : 0,
        ),
    );
  }, [members, user]);

  return (
    <div className="lg:col-span-9">
      {!hasActivePowerOnProSubscription && (
        <Card className="-mt-2 ml-1 mr-3.5 shadow-sm sm:mx-4">
          <CardHeader className="px-4 pb-4 pt-5 sm:p-6 md:p-8">
            <CardTitle className="text-sm sm:text-base md:text-lg">
              Subscribe to PowerOn Pro
              <sup className="font-bold text-brand-accent">+</sup>
            </CardTitle>
            <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2">
              <div className="text-xs sm:text-sm md:text-base">
                Be among the greatest PowerOn developers with an
                <Link
                  href={LIBUM_DOCS_POWERON_PRO_CAPABILITIES_URL}
                  target="_blank"
                  className="mx-1 font-bold text-emerald-400"
                >
                  evolving
                </Link>
                set of powerful tools.
              </div>
              <Button
                variant="secondary"
                className="w-full shrink-0 sm:w-auto"
                onClick={() => {
                  startNavigationFeedback();
                  router.push("/settings/subscriptions");
                }}
              >
                View Subscriptions
              </Button>
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      <div className="mt-6 flex flex-col justify-between gap-4 pl-1 pr-3.5 sm:px-5 lg:flex-row lg:items-center lg:gap-2">
        <div className="flex flex-grow items-center justify-between">
          <h2 className="mt-1.5 flex flex-row gap-2 text-xl font-semibold leading-6 text-gray-1100 dark:text-gray-100 lg:mt-0">
            Users
            <span className="gap-2 text-muted">({users.length})</span>
          </h2>
          <Button
            variant="ghost"
            onClick={debounceRefresh}
            className={`size-8 self-end ${isRefreshBlocked ? "animate-shake" : ""}`}
          >
            <RefreshCcw />
          </Button>
        </div>

        <div className="mt-0 flex flex-grow flex-row items-center gap-2 lg:flex-grow-0">
          <Input
            type="search"
            placeholder="Find a user"
            className="mt-0 w-full lg:w-[400px]"
            id="user-search"
          />
          <Button asChild className="w-fit">
            <Link href="/settings/members">
              <CirclePlus />
              Add Users
            </Link>
          </Button>
        </div>
      </div>
      <div className="lb-scrollbar-overlay -ml-3.5 -mr-1 overflow-x-auto py-6 sm:-mx-6">
        {users.length === 0 && isLoading ? (
          <LoaderCircle className="mx-auto mt-16 flex size-8 animate-spin text-brand-primary" />
        ) : (
          <DataTable
            columns={columns(handleToggleSubscription, loadingMemberId).filter(
              (c: any) =>
                isMobile || isLargeScreen
                  ? ["email", "plusSubscription"].includes(c.accessorKey)
                  : true,
            )}
            data={users}
            searchField={["email", "name"]}
            searchInputId="user-search"
          />
        )}
      </div>
    </div>
  );
}
